import { Alert, Box, Container, List, ListItem, ListItemIcon, Typography, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState, useRef } from 'react';
import LoadingSpinnerCentred from "../Components/LoadingSpinnerCentred";
import CustomMarkdown from "../Components/CustomMarkdown";
import CountdownTimer from "./CountdownTimer";
import ExampleMockExam from "./ExampleMockExam";

// Note to disable mock exams, archive the stripe product via the stripe dashboard
// This should ping the webhook and get the products to refresh, if not, restart the website
export default function MockExamPage(params)
{
    const [product, setProduct] = useState(null);
    const [alertInfo, setAlertInfo] = useState(null);
    const [examSession, setExamSession] = useState(null);
    const [questionPartIndex, setQuestionPartIndex] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    let navigate = useNavigate();
    useEffect(() => fetchMockExamSession(), []);

    function fetchProducts()
    {
        axios.get('api/payments/products').then(result =>
        {
            let mockExamProducts = result.data.filter(p => p.isMockExamToken);
            const emptyProduct = { isEmpty: true, message: "No product available" };
            setProduct(mockExamProducts[0] || emptyProduct);
        }).catch(error =>
        {
            setAlertInfo({ type: "error", message: "Failed to acquire available product information" });
        });
    };

    function fetchMockExamSession()
    {
        axios.get('api/mockexams/current-session', { withCredentials: true }).then(result =>
        {
            if (!result.data.available)
                fetchProducts();
            else setExamSession(result.data);

        }).catch (error =>
        {
            setAlertInfo({ type: "error", message: "Failed to acquire current exam session information" });
        });
    }

    function postStartExam()
    {
        axios.post('api/mockexams/start-session', { withCredentials: true }).then(result =>
        {
            setExamSession(result.data);
        }).catch(error =>
        {
            setAlertInfo({ type: "error", message: "Failed to start exam session" });
        });
    }

    const fileInputRef = useRef(null);
    const handleSubmitExamRequest = () =>
    {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (event) =>
    {
        const file = event.target.files[0];
        if (file)
            postSubmitExam(file);
    };

    function postSubmitExam(file)
    {
        const formData = new FormData();
        formData.append('file', file);
        setSubmitting(true);

        axios.post('api/mockexams/submit', formData, { withCredentials: true }).then(result =>
        {
            setAlertInfo({ type: "success", message: "Successfully submitted exam" });
            setSubmitting(false);
        }).catch(error =>
        {
            setAlertInfo({ type: "error", message: "Failed to submit exam" });
            setSubmitting(false);
        });

    }

    function DisplayUnavailableMockExams() {
        return (
            <Container component="main" maxWidth="md" sx={{ mt: 4 }}>
                <Typography sx={{ marginTop: 8 }} align="center" component="h1" variant="h4" gutterBottom color="primary">
                    Mock Essay Exam
                </Typography>

                <Typography textAlign="center" color="primary" component="h2" variant="h6" sx={{ mt: 4 }}>
                    The mock exam is currently unavailable. It will become available six weeks before the next FRCPath Part 1 exam.
                </Typography>
            </Container>
        );
    }

    function DisplayBuyMockExamOption() {
        return (
            <Container component="main" maxWidth="md" sx={{ mt: 4 }}>
                <Typography sx={{ marginTop: 8 }} align="center" component="h1" variant="h4" gutterBottom color="primary">
                    Mock Essay Exam
                </Typography>

                <List dense={true}>
                    <ListItem disablePadding>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircleIcon sx={{ color: 'green' }} />
                        </ListItemIcon>
                        Mock exams now available
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        The mock exam consists of four essays, just like the real exam.
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        There is one essay each for Malignant Haematology, General Haematology, Transfusion, and Haemostasis and Thrombosis
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        Each essay will be marked by a real person, providing you with bespoke feedback, helping you to pass the hardest part of the exam
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        After purchasing a mock exam, you can use your credit whenever you like prior to the exam, and we aim to provide feedback within 72 hours of submission.
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        Please note that the mock exam is essays only and will not contain multiple choice questions.
                    </ListItem>
                </List>

                <Button onClick={() => navigate('/payment', { state: { productId: product.id } })}
                    fullWidth variant="outlined" sx={{ mt: 2 }}>
                    Purchase Mock Exam
                </Button>

                <ExampleMockExam/>
            </Container>
        );
    }

    function DisplayReadyToStartExam() 
    {
        return (
            <Container component="main" maxWidth="md" sx={{ mt: 4 }}>
                
                <Typography sx={{ marginTop: 8 }} align="center" component="h1" variant="h4" gutterBottom color="primary">
                    Mock Exam
                </Typography>

                <Typography textAlign="center" sx={{ mt: 4 }}>
                    {'You have a pending mock exam token, once started you should aim to complete in 45min, ' +
                    'if you do not complete within this time then you may still submit the exam and it will still be marked'}
                </Typography>

                <Button onClick={postStartExam} fullWidth variant="outlined" sx={{ mt: 4 }}>
                    Start Exam
                </Button>
            </Container>
        );
    }

    function DisplayExamSession()
    {
        //const minutes = Math.floor(timeRemaining / 60);
        //const seconds = timeRemaining % 60;
        //const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        const questionPartText = examSession.questionTextParts[questionPartIndex];

        // Navigate to the next question part
        const incrementQuestionPart = () =>
        {
            if (questionPartIndex < examSession.questionTextParts.length - 1) {
                setQuestionPartIndex(questionPartIndex + 1);
            }
        };

        // Navigate to the previous question part
        const decrementQuestionPart = () =>
        {
            if (questionPartIndex > 0) {
                setQuestionPartIndex(questionPartIndex - 1);
            }
        };

        return (
            <Container component="main" maxWidth="md" sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%', mt : 8 }}>
                    <Typography align="center" component="h1" variant="h4" gutterBottom color="primary">
                        Mock Essay Exam
                    </Typography>

                    <CountdownTimer startTime={examSession.startTime} durationMinutes={45} />
                </Box>

                <Box sx={{ minHeight: '60vh' }}>
                    <CustomMarkdown children={questionPartText} />
                </Box>

                {/* Navigation buttons for Previous and Next */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button
                        sx={{ minWidth: 200 }}
                        onClick={decrementQuestionPart}
                        variant="outlined"
                        disabled={questionPartIndex === 0}>
                        Previous
                    </Button>
                    <Button
                        sx={{ minWidth: 200 }}
                        onClick={incrementQuestionPart}
                        variant="outlined"
                        disabled={questionPartIndex >= examSession.questionTextParts.length - 1}>
                        Next
                    </Button>
                </Box>

                <Button onClick={handleSubmitExamRequest} fullWidth variant="outlined" sx={{ mt: 2 }}>
                    Submit Exam
                </Button>
                {/* Hidden file input */}
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    // Allow common document and text file types:
                    accept=".txt,.doc,.docx,.pdf,.csv,.rtf,.odt"
                />
            </Container>
        );
    }

    if (submitting)
        return <LoadingSpinnerCentred topMargin={true} />

    if (alertInfo) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <Alert severity={alertInfo.type}> {alertInfo.message}</Alert>
            </Box>
        );
    }

    if (product === null && examSession === null)
        return <LoadingSpinnerCentred topMargin={true} />

    if (examSession !== null)
    {
        if (examSession.disabled) // If disabled via the runtime config
            return <DisplayUnavailableMockExams />
        if (examSession.state === "None")
            return <DisplayReadyToStartExam />
        return <DisplayExamSession />
    }

    // If not configured in stripe
    if (product.isEmpty)
        return <DisplayUnavailableMockExams />

    return <DisplayBuyMockExamOption />
}